import { useEffect, useRef, useCallback, useState } from 'react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { X, SwitchCamera, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface BarcodeScannerProps {
  onScan: (barcode: string) => void;
  onClose: () => void;
}

// All supported barcode/QR formats
const SUPPORTED_FORMATS = [
  Html5QrcodeSupportedFormats.QR_CODE,
  Html5QrcodeSupportedFormats.AZTEC,
  Html5QrcodeSupportedFormats.CODABAR,
  Html5QrcodeSupportedFormats.CODE_39,
  Html5QrcodeSupportedFormats.CODE_93,
  Html5QrcodeSupportedFormats.CODE_128,
  Html5QrcodeSupportedFormats.DATA_MATRIX,
  Html5QrcodeSupportedFormats.ITF,
  Html5QrcodeSupportedFormats.EAN_13,
  Html5QrcodeSupportedFormats.EAN_8,
  Html5QrcodeSupportedFormats.PDF_417,
  Html5QrcodeSupportedFormats.UPC_A,
  Html5QrcodeSupportedFormats.UPC_E,
];

export function BarcodeScanner({ onScan, onClose }: BarcodeScannerProps) {
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const isRunningRef = useRef(false);
  const [isStarting, setIsStarting] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [facingMode, setFacingMode] = useState<'environment' | 'user'>('environment');

  const stopScanner = useCallback(async () => {
    if (scannerRef.current && isRunningRef.current) {
      try {
        await scannerRef.current.stop();
      } catch {
        // Ignore stop errors
      }
      isRunningRef.current = false;
    }
  }, []);

  const startScanner = useCallback(async (mode: 'environment' | 'user') => {
    setIsStarting(true);
    setError(null);

    try {
      // Stop existing scanner if running
      await stopScanner();

      // Clear previous instance
      if (scannerRef.current) {
        try {
          scannerRef.current.clear();
        } catch {
          // Ignore clear errors
        }
        scannerRef.current = null;
      }

      const html5Qrcode = new Html5Qrcode('barcode-reader', {
        formatsToSupport: SUPPORTED_FORMATS,
        verbose: false,
      });
      scannerRef.current = html5Qrcode;

      // Calculate qrbox based on viewport
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const qrboxSize = Math.min(viewportWidth * 0.8, viewportHeight * 0.4, 300);

      await html5Qrcode.start(
        { facingMode: mode },
        {
          fps: 15,
          qrbox: { width: qrboxSize, height: qrboxSize },
          disableFlip: false,
        },
        (decodedText) => {
          // Stop scanner on successful scan
          isRunningRef.current = false;
          html5Qrcode.stop().catch(() => {});
          onScan(decodedText);
        },
        () => {
          // Ignore decode errors (happens every frame without barcode)
        }
      );

      isRunningRef.current = true;
      setIsStarting(false);
    } catch (err) {
      console.error('Failed to start scanner:', err);
      setError('Не удалось включить камеру. Проверьте разрешения.');
      setIsStarting(false);
      isRunningRef.current = false;
    }
  }, [onScan, stopScanner]);

  useEffect(() => {
    startScanner(facingMode);

    return () => {
      // Cleanup on unmount
      if (scannerRef.current) {
        if (isRunningRef.current) {
          scannerRef.current.stop().catch(() => {});
        }
        try {
          scannerRef.current.clear();
        } catch {
          // Ignore
        }
        isRunningRef.current = false;
        scannerRef.current = null;
      }
    };
  }, [facingMode, startScanner]);

  const handleSwitchCamera = useCallback(() => {
    setFacingMode((prev) => (prev === 'environment' ? 'user' : 'environment'));
  }, []);

  return (
    <div className="fixed inset-0 z-[100] bg-black flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-black/80 flex-shrink-0">
        <Button
          variant="ghost"
          size="icon"
          className="text-white hover:bg-white/20"
          onClick={onClose}
        >
          <X className="h-6 w-6" />
        </Button>
        <span className="text-white font-medium">Сканировать штрихкод</span>
        <Button
          variant="ghost"
          size="icon"
          className="text-white hover:bg-white/20"
          onClick={handleSwitchCamera}
        >
          <SwitchCamera className="h-6 w-6" />
        </Button>
      </div>

      {/* Scanner area */}
      <div className="flex-1 flex items-center justify-center relative overflow-hidden">
        <div
          id="barcode-reader"
          className="w-full h-full"
          style={{ minHeight: '300px' }}
        />

        {/* Loading overlay */}
        {isStarting && (
          <div className="absolute inset-0 flex items-center justify-center bg-black">
            <div className="text-center">
              <Loader2 className="h-8 w-8 animate-spin text-white mx-auto mb-2" />
              <p className="text-white/80 text-sm">Запуск камеры...</p>
            </div>
          </div>
        )}

        {/* Error overlay */}
        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-black p-4">
            <div className="text-center">
              <p className="text-red-400 mb-4">{error}</p>
              <Button onClick={() => startScanner(facingMode)}>
                Попробовать снова
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Hint */}
      <div className="p-4 bg-black/80 text-center flex-shrink-0">
        <p className="text-white/60 text-sm">
          Наведите камеру на штрихкод или QR-код
        </p>
      </div>
    </div>
  );
}
